// Available themes for the application
// Users can switch between these themes in settings

import { getContrastColor } from '@/utils/colorUtils'

export const themes = {
  // Default Material Design theme
  default: {
    name: 'Domyślny',
    light: {
      primary: '#2A587F',
      secondary: '#3A383E',
      accent: '#2A587F',
      info: '#007AFF',
      success: '#34C759',
      warning: '#FFCC00',
      error: '#FF3B30',
      drawer: '#FBFAFD',
      drawerSelection: '#E7E6E9',
      background: '#FAFAFA',  // Soft off-white reduces eye strain vs pure white
      surface: '#FFFFFF',     // Slightly lighter for subtle elevation
    },
    dark: {
      primary: '#4A9EFF',
      secondary: '#b0bec5',
      accent: '#4A9EFF',
      info: '#0A84FF',
      success: '#30D158',
      warning: '#FF9F0A',
      error: '#FF453A',
      drawer: '#23232b',
      drawerSelection: '#424250',
      background: '#121212',  // Material Design recommended dark background
      surface: '#1E1E1E',     // Elevated surfaces slightly lighter
    },
  },
  material: {
    name: 'Material',
    light: {
      primary: '#2A587F',
      secondary: '#3A383E',
      accent: '#2A587F',
      info: '#007AFF',
      success: '#34C759',
      warning: '#FFCC00',
      error: '#FF3B30',
      drawer: '#FBFAFD',
      drawerSelection: '#E7E6E9',
      background: '#FAFAFA',
      surface: '#F5F9FF',
    },
    dark: {
      primary: '#4A9EFF',
      secondary: '#b0bec5',
      accent: '#4A9EFF',
      info: '#0A84FF',
      success: '#30D158',
      warning: '#FF9F0A',
      error: '#FF453A',
      drawer: '#23232b',
      drawerSelection: '#424250',
      background: '#121212',
      surface: '#152635',
    },
  },

  // High contrast theme for better visibility
  highContrast: {
    name: 'Wysoki kontrast',
    light: {
      primary: '#000000',
      secondary: '#424242',
      accent: '#000000',
      info: '#0066CC',
      success: '#2E7D32',
      warning: '#F57C00',
      error: '#C62828',
      drawer: '#FFFFFF',
      drawerSelection: '#E0E0E0',
      background: '#FFFFFF',
      surface: '#F5F5F5',
    },
    dark: {
      primary: '#FFFFFF',
      secondary: '#E0E0E0',
      accent: '#FFFFFF',
      info: '#64B5F6',
      success: '#81C784',
      warning: '#FFB74D',
      error: '#E57373',
      drawer: '#000000',
      drawerSelection: '#424242',
      background: '#000000',
      surface: '#212121',
    },
  },

  // Warm theme with orange/brown tones
  warm: {
    name: 'Ciepły',
    light: {
      primary: '#D84315',
      secondary: '#5D4037',
      accent: '#FF6F00',
      info: '#0288D1',
      success: '#388E3C',
      warning: '#F57C00',
      error: '#D32F2F',
      drawer: '#FFF3E0',
      drawerSelection: '#FFE0B2',
      background: '#FFF8E1',
      surface: '#FFF8E1',
    },
    dark: {
      primary: '#FF8A65',
      secondary: '#A1887F',
      accent: '#FFB74D',
      info: '#4FC3F7',
      success: '#81C784',
      warning: '#FFB74D',
      error: '#E57373',
      drawer: '#1A1410',
      drawerSelection: '#3D2B1F',
      background: '#1A1410',
      surface: '#2C2319',
    },
  },

  // Cool theme with blue/purple tones
  cool: {
    name: 'Chłodny',
    light: {
      primary: '#1976D2',
      secondary: '#424242',
      accent: '#7C4DFF',
      info: '#0288D1',
      success: '#00897B',
      warning: '#FF6F00',
      error: '#C62828',
      drawer: '#E8F4F8',
      drawerSelection: '#BBDEFB',
      background: '#E3F2FD',
      surface: '#F5F9FF',
    },
    dark: {
      primary: '#64B5F6',
      secondary: '#90A4AE',
      accent: '#B388FF',
      info: '#4FC3F7',
      success: '#4DB6AC',
      warning: '#FFB74D',
      error: '#E57373',
      drawer: '#0A1929',
      drawerSelection: '#1A3A52',
      background: '#0A1929',
      surface: '#152635',
    },
  },
}

// eslint-disable-next-line no-unused-vars
const unusedThemes = {
  // Nature theme with green tones
  nature: {
    name: 'Natura',
    light: {
      primary: '#2E7D32',
      secondary: '#558B2F',
      accent: '#66BB6A',
      info: '#0288D1',
      success: '#388E3C',
      warning: '#F9A825',
      error: '#D32F2F',
      drawer: '#F1F8E9',
      drawerSelection: '#DCEDC8',
      background: '#F1F8E9',
      surface: '#F5FAF0',
    },
    dark: {
      primary: '#66BB6A',
      secondary: '#9CCC65',
      accent: '#81C784',
      info: '#4FC3F7',
      success: '#81C784',
      warning: '#FFD54F',
      error: '#E57373',
      drawer: '#0D1F0C',
      drawerSelection: '#1B3A19',
      background: '#0D1F0C',
      surface: '#12250F',
    },
  },

  // Monochrome theme (black and white with gray shades)
  monochrome: {
    name: 'Monochromatyczny',
    light: {
      primary: '#424242',
      secondary: '#757575',
      accent: '#616161',
      info: '#424242',
      success: '#616161',
      warning: '#757575',
      error: '#212121',
      red: '#2F2F2F',
      blue: '#2F2F2F',
      orange: '#2F2F2F',
      yellow: '#2F2F2F',
      green: '#2F2F2F',
      teal: '#2F2F2F',
      cyan: '#2F2F2F',
      drawer: '#FAFAFA',
      drawerSelection: '#E0E0E0',
      background: '#FAFAFA',
      surface: '#FFFFFF',
    },
    dark: {
      primary: '#E0E0E0',
      secondary: '#BDBDBD',
      accent: '#EEEEEE',
      info: '#E0E0E0',
      success: '#BDBDBD',
      warning: '#9E9E9E',
      error: '#F5F5F5',
      red: '#2F2F2F',
      blue: '#2F2F2F',
      orange: '#2F2F2F',
      yellow: '#2F2F2F',
      green: '#2F2F2F',
      teal: '#2F2F2F',
      cyan: '#2F2F2F',
      drawer: '#121212',
      drawerSelection: '#2C2C2C',
      background: '#121212',
      surface: '#1E1E1E',
    },
  },
}

// Enrich themes with computed `on-<color>` entries and derived colors at module load time.
// This ensures `on-*` colors and derived colors are available immediately (before components mount)
const AUTO_ON_KEYS = [
  'primary',
  'secondary',
  'accent',
  'info',
  'success',
  'warning',
  'error',
  'background',
  'surface',
  'drawer',
  'drawerSelection',
  'details',
  'critical',
  'red',
  'blue',
  'orange',
  'yellow',
  'green',
  'teal',
  'cyan',
  'messageCurrentUserBg',
  'messageOtherUserBg',
  'messageImportantBg',
]

Object.keys(themes).forEach(themeKey => {
  const t = themes[themeKey]
  if (!t) return
  ;['light', 'dark'].forEach(mode => {
    const modeObj = t[mode]
    if (!modeObj) return

    // Derive colors based on the theme's palette (if not already defined)
    if (!modeObj.details) {
      // details: lighter/darker version of secondary
      modeObj.details = mode === 'light' ? '#666666' : '#bdbdbd'
    }

    if (!modeObj.critical) {
      // critical: use error color or a bright red
      modeObj.critical = modeObj.error || (mode === 'light' ? '#FF1212' : '#FF453A')
    }

    // Message colors: derive from primary and surface
    if (!modeObj.messageCurrentUserBg) {
      modeObj.messageCurrentUserBg = modeObj.primary
    }
    if (!modeObj.messageCurrentUserText) {
      modeObj.messageCurrentUserText = getContrastColor(modeObj.messageCurrentUserBg)
    }

    if (!modeObj.messageOtherUserBg) {
      // Light gray for light mode, dark gray for dark mode
      modeObj.messageOtherUserBg = mode === 'light' ? '#E9E9EB' : '#3C3C43'
    }
    if (!modeObj.messageOtherUserText) {
      modeObj.messageOtherUserText = getContrastColor(modeObj.messageOtherUserBg)
    }

    if (!modeObj.messageImportantBg) {
      modeObj.messageImportantBg = modeObj.success
    }
    if (!modeObj.messageImportantText) {
      modeObj.messageImportantText = getContrastColor(modeObj.messageImportantBg)
    }

    // Compute on-* colors
    AUTO_ON_KEYS.forEach(k => {
      const onKey = `on-${k}`
      if (modeObj[onKey]) return
      if (modeObj[k]) {
        try {
          modeObj[onKey] = getContrastColor(modeObj[k])
        } catch (e) {
          // ignore
        }
      }
    })
  })
})

// Helper function to get theme by key
export function getTheme(themeKey) {
  return themes[themeKey] || themes.default
}

// Helper function to get all theme names for UI selection
export function getThemesList() {
  return Object.keys(themes).map(key => ({
    key,
    name: themes[key].name,
  }))
}
